import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import { AppModal, ModalType } from '../components/AppModal';
import ApiService from '../services/ApiService';
import { Employee } from '../models/types';
import { formatCurrency } from '../utils/helpers';

const EmployeeListScreen = ({ navigation }: any) => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const loadEmployees = async () => {
        setLoading(true);
        try {
            const data = await ApiService.getEmployees();
            setEmployees(data);
        } catch (error) {
            console.error('Error loading employees:', error);
        } finally {
            setLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadEmployees();
        }, [])
    );

    const handleDeleteEmployee = (employee: Employee) => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Employee',
            message: `Are you sure you want to delete "${employee.name}"? This will also delete all salary records.`,
            onConfirm: async () => {
                setDeleteLoading(true);
                try {
                    await ApiService.deleteEmployee(employee.id);
                    setModal(prev => ({ ...prev, visible: false }));
                    loadEmployees();
                } catch (error) {
                    console.error('Error deleting employee:', error);
                    setModal({
                        visible: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to delete employee.',
                    });
                } finally {
                    setDeleteLoading(false);
                }
            },
        });
    };

    const renderEmployeeItem = ({ item }: { item: Employee }) => {
        return (
            <TouchableOpacity
                style={styles.employeeCard}
                onPress={() => navigation.navigate('EmployeeDetail', { employeeId: item.id })}
                activeOpacity={0.7}
            >
                <View style={styles.iconContainer}>
                    <Icon name="account" size={28} color="#2196F3" />
                </View>

                <View style={styles.employeeContent}>
                    <View style={styles.employeeHeader}>
                        <Text style={styles.employeeName}>{item.name}</Text>
                        <View style={[
                            styles.statusBadge,
                            { backgroundColor: item.isActive ? '#E8F5E9' : '#FFEBEE' }
                        ]}>
                            <Text style={[
                                styles.statusText,
                                { color: item.isActive ? '#4CAF50' : '#F44336' }
                            ]}>
                                {item.isActive ? 'Active' : 'Inactive'}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.employeeDetails}>
                        <View style={styles.detailRow}>
                            <Icon name="currency-inr" size={14} color={theme.colors.textSecondary} />
                            <Text style={styles.salary}>
                                Fixed: {formatCurrency(item.fixedSalary)}/month
                            </Text>
                        </View>
                        {item.phone && (
                            <View style={styles.detailRow}>
                                <Icon name="phone" size={14} color={theme.colors.textSecondary} />
                                <Text style={styles.phone}>{item.phone}</Text>
                            </View>
                        )}
                    </View>
                </View>

                <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={() => handleDeleteEmployee(item)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                    <Icon name="trash-can-outline" size={20} color={theme.colors.error} />
                </TouchableOpacity>
            </TouchableOpacity>
        );
    };

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <Icon name="account-group-outline" size={64} color={theme.colors.textLight} />
            <Text style={styles.emptyTitle}>No Employees Found</Text>
            <Text style={styles.emptyText}>
                Add employees to track their salaries
            </Text>
        </View>
    );

    const totalSalary = employees.reduce((sum, emp) => sum + (emp.isActive ? emp.fixedSalary : 0), 0);
    const activeCount = employees.filter(emp => emp.isActive).length;

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader title="Employee Salary" showBackButton={true} navigation={navigation} />

            {/* Summary Card */}
            <View style={styles.summaryCard}>
                <View style={styles.summaryIconBg}>
                    <Icon name="account-cash" size={24} color="#2196F3" />
                </View>
                <View style={styles.summaryContent}>
                    <Text style={styles.summaryLabel}>Monthly Salary Budget</Text>
                    <Text style={styles.summaryAmount}>{formatCurrency(totalSalary)}</Text>
                </View>
                <View style={styles.summaryBadge}>
                    <Icon name="account-multiple" size={16} color={theme.colors.primary} />
                    <Text style={styles.summaryCount}>{activeCount}</Text>
                </View>
            </View>

            {/* Employee List */}
            <FlashList
                data={employees}
                keyExtractor={(item) => item.id}
                renderItem={renderEmployeeItem}
                contentContainerStyle={styles.listContent}
                ListEmptyComponent={renderEmpty}
                refreshing={loading}
                onRefresh={loadEmployees}
            />

            {/* FAB */}
            <TouchableOpacity
                style={styles.fab}
                onPress={() => navigation.navigate('AddEditEmployee', {})}
            >
                <Icon name="plus" size={28} color="#fff" />
            </TouchableOpacity>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
                loading={deleteLoading}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    summaryCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        ...theme.shadows.sm,
    },
    summaryIconBg: {
        width: 48,
        height: 48,
        borderRadius: theme.borderRadius.md,
        backgroundColor: '#E3F2FD',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    summaryContent: {
        flex: 1,
    },
    summaryLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 2,
    },
    summaryAmount: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
    },
    summaryBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.primary + '15',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: theme.borderRadius.round,
        gap: 4,
    },
    summaryCount: {
        ...theme.typography.caption,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    listContent: {
        paddingHorizontal: theme.spacing.md,
        paddingBottom: 100,
    },
    employeeCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        borderLeftWidth: 4,
        borderLeftColor: '#2196F3',
        ...theme.shadows.sm,
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: theme.borderRadius.md,
        backgroundColor: '#E3F2FD',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    employeeContent: {
        flex: 1,
    },
    employeeHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    employeeName: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        flex: 1,
        marginRight: theme.spacing.sm,
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: theme.borderRadius.sm,
    },
    statusText: {
        ...theme.typography.caption,
        fontWeight: '600',
    },
    employeeDetails: {
        gap: 4,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    salary: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    phone: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    deleteButton: {
        padding: theme.spacing.xs,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 100,
    },
    emptyTitle: {
        ...theme.typography.h5,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    emptyText: {
        ...theme.typography.body2,
        color: theme.colors.textLight,
        marginTop: theme.spacing.xs,
    },
    fab: {
        position: 'absolute',
        right: theme.spacing.lg,
        bottom: theme.spacing.lg,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        ...theme.shadows.md,
    },
});

export default EmployeeListScreen;
